import React, { useRef, useState, useEffect, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import ReactToPrint from "react-to-print";
import { FaPrint } from "react-icons/fa6";
import QRCode from "qrcode.react";
import "./Certificate.css";
import certLogo from "../images/cert-logo.png";
// import signatureImg from "../images/signature.png";
import { GET_MEMBER_WITH_PARENT } from "../utils/familyQueries";

// ── localStorage cache ────────────────────────────────────────────────────────
// Stores the full resolved lineage chain per memberId so repeat visits are
// instant. A 7-day TTL is generous for family-tree data that rarely changes.
// A background refresh (stale-while-revalidate) silently updates the cache.

const LINEAGE_CACHE_PREFIX = "albaalawy_lineage_";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getLineageCache(memberId) {
  try {
    const raw = localStorage.getItem(`${LINEAGE_CACHE_PREFIX}${memberId}`);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
      localStorage.removeItem(`${LINEAGE_CACHE_PREFIX}${memberId}`);
      return null;
    }
    return entry; // { ts, lineage: [{id,name}], memberData }
  } catch {
    return null;
  }
}

function setLineageCache(memberId, lineage, memberData) {
  try {
    localStorage.setItem(`${LINEAGE_CACHE_PREFIX}${memberId}`, JSON.stringify({ ts: Date.now(), lineage, memberData }));
  } catch {
    // Ignore quota / access errors — caching is best-effort
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const Certificate = ({ memberId }) => {
  const [lineage, setLineage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const certificateRef = useRef();
  const previewWrapperRef = useRef();
  const applyScaleRef = useRef(null);
  const client = useApolloClient();

  useEffect(() => {
    const controller = new AbortController();

    // Walks up the ancestry chain via sequential GraphQL queries.
    // silent=true → update state & cache without touching loading indicators
    // (used for background refresh when serving from cache).
    async function doNetworkFetch(silent) {
      let currentMemberId = memberId;
      const lineageArray = [];
      let fetchedMemberData = null;
      let fetchedCount = 0;
      const estimatedAncestors = 30;

      // First query: show member info quickly before the full chain loads
      const { data: initialData } = await client.query({
        query: GET_MEMBER_WITH_PARENT,
        variables: { id: memberId },
        fetchPolicy: "cache-first",
      });
      const initialMember = initialData?.familyMember?.data;
      if (initialMember && !silent) {
        setMemberData(initialMember.attributes);
        setLoading(false);
        setLoadingProgress(5);
      }

      // Walk up the ancestor chain — no artificial delay, Apollo cache-first
      // avoids a network round-trip for any member already seen this session.
      while (currentMemberId && !controller.signal.aborted) {
        const { data } = await client.query({
          query: GET_MEMBER_WITH_PARENT,
          variables: { id: currentMemberId },
          fetchPolicy: "cache-first",
        });

        const member = data?.familyMember?.data;
        if (!member) break;

        if (currentMemberId === memberId && !fetchedMemberData) {
          fetchedMemberData = member.attributes;
          if (!silent) setMemberData(fetchedMemberData);
        }

        lineageArray.push({ id: member.id, name: member.attributes.name });
        fetchedCount++;
        if (!silent) {
          setLoadingProgress(Math.min(5 + Math.round((fetchedCount / estimatedAncestors) * 95), 99));
        }

        const parentData = member.attributes.parent?.data;
        currentMemberId = parentData ? parentData.id : null;
      }

      // Bail out if the component unmounted during the traversal
      if (controller.signal.aborted) return;

      const reversed = lineageArray.reverse();

      // Persist fresh data so next visit is instant
      if (fetchedMemberData) {
        setLineageCache(memberId, reversed, fetchedMemberData);
      }

      // Always update display state (silent refresh still refreshes the tree)
      setLineage(reversed);
      if (fetchedMemberData) setMemberData(fetchedMemberData);
      setLoadingProgress(100);
      if (!silent) setLoading(false);
    }

    async function fetchLineage() {
      if (!memberId) {
        setLoading(false);
        return;
      }

      // ── Fast path: serve from localStorage immediately ──────────────────
      const cached = getLineageCache(memberId);
      if (cached) {
        setLineage(cached.lineage);
        setMemberData(cached.memberData);
        setLoadingProgress(100);
        setLoading(false);
        // Stale-while-revalidate: silently refresh in the background so the
        // cache stays up-to-date without making the user wait.
        doNetworkFetch(true).catch(() => {});
        return;
      }

      // ── Slow path: no cache yet, fetch fresh ────────────────────────────
      try {
        await doNetworkFetch(false);
      } catch (err) {
        console.error("Error fetching lineage:", err);
        setError(err);
        setLoading(false);
      }
    }

    fetchLineage();

    return () => {
      controller.abort();
    };
  }, [memberId, client]);

  const lineageNames = lineage.map((item) => item.name);

  // ── Responsive preview scaling ────────────────────────────────────────────
  // The certificate always renders at a fixed A4 width (794 px) so fonts,
  // lineage bubbles and the QR code keep their print proportions. A CSS
  // transform: scale() then shrinks the whole thing to fit the container on
  // narrow screens. This avoids font-size / QR-code responsive hacks.

  // Set up ResizeObserver once the certificate DOM is mounted.
  useEffect(() => {
    if (!memberData) return;
    const cert = certificateRef.current;
    const wrapper = previewWrapperRef.current;
    if (!cert || !wrapper) return;

    function applyScale() {
      const scale = Math.min(wrapper.offsetWidth / 794, 1);
      if (scale < 1) {
        cert.style.transform = `scale(${scale})`;
        cert.style.transformOrigin = "top left";
        wrapper.style.height = Math.ceil(cert.scrollHeight * scale) + "px";
      } else {
        cert.style.transform = "";
        cert.style.transformOrigin = "";
        wrapper.style.height = "";
      }
    }

    applyScaleRef.current = applyScale;
    // Wrap in rAF so style mutations happen outside the observation cycle,
    // preventing "ResizeObserver loop completed with undelivered notifications".
    const ro = new ResizeObserver(() => requestAnimationFrame(applyScale));
    ro.observe(wrapper);
    applyScale();
    return () => ro.disconnect();
  }, [memberData]);

  // Re-measure whenever lineage content finishes loading (cert height changes).
  useEffect(() => {
    if (applyScaleRef.current) requestAnimationFrame(applyScaleRef.current);
  }, [lineage]);
  // ─────────────────────────────────────────────────────────────────────────

  // Before printing: force the certificate to A4 width so content reflows to
  // print proportions (critical on mobile where the viewport is narrower than
  // A4). Then zoom down if the content is still taller than one A4 page.
  const handleBeforeGetContent = useCallback(() => {
    return new Promise((resolve) => {
      const cert = certificateRef.current;
      if (!cert) {
        resolve();
        return;
      }
      // Clear display scale so ReactToPrint clones the cert at its natural
      // A4 width (794 px from CSS) without any screen transform applied.
      cert.style.transform = "";
      cert.style.transformOrigin = "";
      cert.style.zoom = "";
      cert.style.minHeight = "1123px";

      // Double rAF ensures a full reflow before we measure.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const A4_HEIGHT_PX = 1123; // 297 mm × 3.7795 px/mm at 96 dpi
          const naturalHeight = cert.scrollHeight;
          if (naturalHeight > A4_HEIGHT_PX) {
            cert.style.zoom = (A4_HEIGHT_PX / naturalHeight).toFixed(4);
          }
          resolve();
        });
      });
    });
  }, []);

  const handleAfterPrint = useCallback(() => {
    const cert = certificateRef.current;
    if (!cert) return;
    cert.style.zoom = "";
    cert.style.minHeight = "";
    // Restore the responsive display scale.
    requestAnimationFrame(() => {
      if (applyScaleRef.current) applyScaleRef.current();
    });
  }, []);

  if (error)
    return (
      <div className="text-center p-4">
        <p className="text-red-500 font-semibold">Error: {error.message}</p>
        <p>There was an error loading the certificate. Please try again later.</p>
      </div>
    );

  return (
    <div className="">
      <ReactToPrint
        trigger={() => (
          <button
            variant="secondary"
            className="flex gap-2 mb-5 items-center text-xs md:text-sm py-2 px-5 border rounded-lg hover:bg-indigo-50"
            disabled={loading || lineageNames.length === 0}
          >
            <FaPrint title="print certificate" className="size-2 md:size-4 mr-2 cursor-pointer" />
            Print Certificate
          </button>
        )}
        content={() => certificateRef.current}
        onBeforeGetContent={handleBeforeGetContent}
        onAfterPrint={handleAfterPrint}
        documentTitle="Family Chain Certificate"
        pageStyle={`
          @page { size: A4 portrait; margin: 0; }
          html, body { width: 210mm; height: 297mm; margin: 0; padding: 0; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        `}
      />

      {/* Loading indicator */}
      {(loading || loadingProgress < 100) && lineageNames.length === 0 && (
        <div className="text-center mb-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p>Loading certificate data...</p>
          {loadingProgress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 max-w-md mx-auto mt-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${loadingProgress}%` }}
              ></div>
              <p className="text-xs mt-1">Loading ancestry: {loadingProgress}%</p>
            </div>
          )}
        </div>
      )}

      {/* Progress indicator while background refresh is running */}
      {!loading && lineageNames.length > 0 && loadingProgress < 100 && (
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">Loading full ancestry tree: {loadingProgress}%</p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 max-w-md mx-auto">
            <div
              className="bg-green-500 h-1.5 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${loadingProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Certificate content */}
      {memberData && (
        <div ref={previewWrapperRef} className="certificate-preview-wrapper">
        <div ref={certificateRef} className="certificate">
          <div className="certificate-wrapper">
            <div className="certificate-header">
              <img src={certLogo} className="cert-logo w-20 lg:w-[148px] m-auto" alt="cert-logo" />
              <h1 className="certificate-title arabika text-xl lg:text-2xl xl:text-xl text-center font-playwrite">
                بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ
              </h1>
              <h4 className=" arabika-big text-xs lg:text-sm xl:text-xl mb-5" dir="rtl">
                هذا ما تم إثباته بالمصادر المعتمدة في علم الأنساب بخصوص نسب{" "}
                <strong className="" style={{ color: "#6baf02" }}>
                  {memberData.name}
                </strong>
                ، والذي تمتد سلسلته كما يلي من الشجرة الأم التي جمعها النسابة الحبيب علي زين العابدين بن عبدالله
                العيدروس.
              </h4>
            </div>
            <div className="certificate-content">
              <div className="lineage flex flex-wrap justify-center font-times mb-10">
                {lineageNames.map((name, index) => (
                  <div className="lineage-item flex items-center" key={index}>
                    <p className="lineage-name text-xs lg:text-sm bg-lime-400 mr-2 border-2 rounded-full p-2">{name}</p>
                    {index < lineageNames.length - 1 && (
                      <p className="mr-2">
                        <span className="indexNumber bg-slate-500 text-white px-4 py-0">{index + 1}</span>
                      </p>
                    )}
                  </div>
                ))}

                {loadingProgress < 100 && lineageNames.length > 0 && (
                  <div className="lineage-item flex items-center">
                    <p className="lineage-name text-xs lg:text-sm bg-gray-200 mr-2 border-2 rounded-full p-2 animate-pulse">
                      Loading...
                    </p>
                  </div>
                )}
              </div>

              {/* Bio Details Table */}
              <div className="bio-details w-[90%] mx-auto print:w-full">
                <h4 className="lineage-table-title text-xl lg:text-2xl text-center mb-1">البيانات للسادة آل باعلوي </h4>
                <table
                  className="bio-table w-full border-collapse overflow-x-scroll border border-black text-xs lg:text-sm"
                  dir="rtl"
                >
                  <tbody>
                    <tr>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">رقم التسجيل</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        RBLW-000{memberId || "N/A"}
                      </td>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">مقاطعة</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.county || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">القبيلة</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.clan || "N/A"}
                      </td>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">الجنسية</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.nationality || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">الذرية</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.posterity || "N/A"}
                      </td>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">العنوان</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.address || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="arabika p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        قبيلة الأم
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.mothers_clan || "N/A"}
                      </td>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">البريد الإلكتروني</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.email || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">تاريخ الميلاد ومكانه</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {memberData.date_of_birth && memberData.place_of_birth
                          ? `${memberData.date_of_birth}, ${memberData.place_of_birth}`
                          : memberData.date_of_birth || memberData.place_of_birth || "N/A"}
                      </td>

                      {/* add here */}
                      <td className="p-2 border bg-lime-200 border-black text-right" colSpan={2}>
                        <small className="block arabika">تاريخ التسجيل</small>
                      </td>
                      <td className="p-2 border border-black text-left" colSpan={2}>
                        {new Date().toLocaleDateString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="arabika-mid float-end mt-3" style={{ float: "right" }}>
                  الإشراف والتعضيد : الحبيب محمد بن عبدالرحمن السقاف.
                </p>
              </div>
            </div>
            <div className="certificate-footer flex justify-between items-center">
              <div className="w-3/4">
                <p className="arabika-small text-center mb-6" dir="rtl">
                  السيد صالح العارف بن محمد البيض
                </p>
                <p className="arabika-small text-center" dir="rtl">
                  محمد عبدالله محمد السقاف <br />
                  (خادم أنساب السادة آل باعلوي - شرق إفريقيا)
                </p>
              </div>
              <div className="w-1/4 qr-code-container flex flex-col items-center" style={{ float: "right" }}>
                <QRCode
                  value={`ALBAALAWY FAMILY CERTIFICATE: NAME: -- ${memberData.name} --, EMAIL: -- ${
                    memberData.email || "N/A"
                  } --, DATE OF BIRTH: ${memberData.date_of_birth || "N/A"}, CERTIFIED BY: Albaalawy, VERIFIED BY: `}
                  className="qr-code w-[80px] h-auto"
                />
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Certificate;
